import { useMemo } from 'react';
import { useTheme, Box, alpha } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import RiskBadge from './RiskBadge';
import FlagChips from './FlagChips';

const pct = (v) => (v != null ? `${(v * 100).toFixed(0)}%` : '-');
const num = (v) => (v != null ? v.toFixed(3) : '-');

const AnomalyPairGrid = ({ rows = [], loading = false }) => {
  const theme = useTheme();

  const columns = useMemo(() => [
    {
      field: 'risk',
      headerName: 'Risk',
      width: 80,
      renderCell: ({ value }) => <RiskBadge risk={value} />,
    },
    {
      field: 'userId',
      headerName: 'User',
      width: 200,
      renderCell: ({ value }) => (
        <Box sx={{ color: 'primary.main' }}>{value}</Box>
      ),
    },
    {
      field: 'crewCode',
      headerName: 'Crew',
      width: 80,
      renderCell: ({ value }) => (
        <Box sx={{ color: 'secondary.main' }}>{value}</Box>
      ),
    },
    { 
      field: 'pairCount',        
      headerName: 'Count',      
      width: 70,  
      type: 'number' 
    },
    { 
      field: 'userTotalChanges', 
      headerName: 'User Total', 
      width: 70,  
      type: 'number' 
    },
    { 
      field: 'crewTotalChanges', 
      headerName: 'Crew Total', 
      width: 70,  
      type: 'number' 
    },
    {
      field: 'relativeFreqByUser',
      headerName: '% of User',
      align: 'right',
      width: 70,
      renderCell: ({ value }) => pct(value),
    },
    {
      field: 'relativeFreqByCrew',
      headerName: '% of Crew',
      align: 'right',
      width: 70,
      renderCell: ({ value }) => pct(value),
    },
    {
      field: 'offHoursRatio',
      headerName: 'Off-hours',
      align: 'right',
      width: 70,
      renderCell: ({ value }) => pct(value),
    },
    {
      field: 'retroactiveRatio',
      headerName: 'Retroactive',
      align: 'right',
      width: 70,
      renderCell: ({ value }) => pct(value),
    },
    {
      field: 'lastMinuteRatio',
      headerName: '% Last-min',
      align: 'right',
      width: 70,
      renderCell: ({ value }) => pct(value),
    },
    {
      field: 'scores',
      headerName: 'IF Score',
      align: 'right',
      width: 70,
      renderCell: ({ value }) => (
        <Box>
          {num(value?.isolationForest)}
        </Box>
      ),
      sortComparator: (a, b) => (b?.combined ?? 0) - (a?.combined ?? 0),
    },
    {
      field: 'flags',
      headerName: 'Flags',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: ({ value }) => {
        return (
          <Box
            sx={{
              height: '100%',
              alignItems: 'flex-start',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <FlagChips flags={value} max={2} />
          </Box>
        )
      }
    },
  ], []);

  const RISK_BG = {
    CRITICAL: alpha(theme.palette.error.main, 0.12),
    HIGH:     alpha('#f57c00', 0.10),
    MEDIUM:   alpha('#f9a825', 0.08),
    LOW:      'transparent',
  };


  return (
    <DataGrid
      rows={rows.map((r, i) => ({ id: i, ...r }))}
      columns={columns}
      loading={loading}
      density="compact"
      disableRowSelectionOnClick
      initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
      pageSizeOptions={[25, 50, 100]}
      getRowClassName={({ row }) => `risk-${row.risk}`}
      sx={{
        border: 'none',
        '& .risk-CRITICAL': { bgcolor: RISK_BG.CRITICAL },
        '& .risk-HIGH':     { bgcolor: RISK_BG.HIGH },
        '& .risk-MEDIUM':   { bgcolor: RISK_BG.MEDIUM },
        '& .MuiDataGrid-columnHeaders': {
          bgcolor: theme.palette.background.alt ?? theme.palette.background.paper,
        },
      }}
    />
  );
};

export default AnomalyPairGrid;
